import { StarIcon } from '@/assets/icons/icons';
import React, { ReactNode, HTMLAttributes } from 'react';

import styles from './Pill.module.css';

interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  /**
   * The colour, when the text cannot say it: translated text no longer matches
   * the English key the colour is otherwise taken from.
   */
  variant?: ThemeKey;
}

const THEME_KEYS = [
  'resolved',
  'processing',
  'needs_attention',
  'reply_needed',
  'default',
  'resolved_via_ai',
] as const;

type ThemeKey = (typeof THEME_KEYS)[number];

const isThemeKey = (key: string): key is ThemeKey =>
  (THEME_KEYS as readonly string[]).includes(key);

const norm = (v: string): string =>
  String(v ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const Pill: React.FC<PillProps> = ({
  children,
  variant,
  className,
  ...rest
}) => {
  const raw = String(children ?? '').trim();
  if (!raw) return null;

  const key = variant ?? norm(raw);
  const theme: ThemeKey = isThemeKey(key) ? key : 'default';
  const showIcon = key === 'resolved_via_ai';

  // A caller's `style` arrives in `rest`, so it still wins over the defaults.
  return (
    <span
      className={[styles.pill, styles[theme], className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {showIcon && <StarIcon height={12} width={10} />}

      {raw}
    </span>
  );
};

export default Pill;
